import { memo } from 'react';

import Box from '@zenbusiness/zui/Box';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Skeleton from '@zenbusiness/zui/Skeleton';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';

import useBreakpoints from '#client/hooks/useBreakpoints';
import useMitekSdkContext from '#client/hooks/useMitekSdk';
import useModalContext from '#client/hooks/useModalContext';

import {
  BoxStyled,
  CapturedImageWrapperStyled,
  ImageContainerStyled,
  ImageStyled
} from './CapturedImage.styles';
import IntroCaptureBackImage from './IntroCaptureBackImage';
import ViewCapturedModal from './ViewCapturedModal';
import WarningMessage from './WarningMessage';

import { CAPTURE_BACK_SIDE } from '../constants/captureSide';

const CapturedImage = ({
  sideType,
  imageData = '',
  saveCapturedImageHandler,
  isDisable = false,
  loading = false,
  errorMessage,
  warningMessage
}) => {
  const theme = useTheme();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();
  const title = `${sideType === CAPTURE_BACK_SIDE ? 'Back' : 'Front'} of check`;
  const { openModal } = useModalContext();
  const {
    autoCaptureCheckImageFront,
    autoCaptureCheckImageBack,
    manualCaptureCheckImageBack,
    manualCaptureCheckImageFront,
    isAutoCaptureMode
  } = useMitekSdkContext();

  const touchCaptureBackImage = () => {
    if (isDisable) {
      return;
    }

    openModal({
      component: IntroCaptureBackImage,
      isAutoCaptureMode,
      autoCaptureCheckImageBack,
      manualCaptureCheckImageBack,
      saveCapturedImageHandler
    });
  };

  const captureCheckImageFront = () =>
    isAutoCaptureMode
      ? autoCaptureCheckImageFront(saveCapturedImageHandler)
      : manualCaptureCheckImageFront(saveCapturedImageHandler);

  const takePhotoHandler = () => {
    if (isDisable) {
      return;
    }

    if (sideType === CAPTURE_BACK_SIDE) {
      touchCaptureBackImage();
    } else {
      captureCheckImageFront();
    }
  };

  const retakePhotoHandler = () => {
    if (isAutoCaptureMode) {
      if (sideType === CAPTURE_BACK_SIDE) {
        autoCaptureCheckImageBack(saveCapturedImageHandler);
      } else {
        autoCaptureCheckImageFront(saveCapturedImageHandler);
      }
    } else if (sideType === CAPTURE_BACK_SIDE) {
      manualCaptureCheckImageBack(saveCapturedImageHandler);
    } else {
      manualCaptureCheckImageFront(saveCapturedImageHandler);
    }
  };

  const openImageViewModal = () => {
    if (isDisable) {
      return;
    }

    openModal({
      component: ViewCapturedModal,
      handleRetake: retakePhotoHandler,
      base64Image: imageData,
      title
    });
  };

  return (
    <Stack
      aria-disabled={isDisable}
      aria-label={title}
      direction="column"
      gap="16px"
    >
      <CapturedImageWrapperStyled isDisable={isDisable}>
        <Text
          color="primary"
          size={isDesktop ? 'lg' : 'md'}
        >
          {title}
        </Text>
        {imageData && !loading && (
          <ImageContainerStyled
            aria-label="image"
            onClick={openImageViewModal}
          >
            <ImageStyled
              alt={title}
              src={imageData}
            />
          </ImageContainerStyled>
        )}
        {!imageData && !loading && (
          <BoxStyled
            aria-label="take-photo-box"
            isDisable={isDisable}
            onClick={takePhotoHandler}
          >
            <Box>
              <SharedAsset
                alt=""
                color={theme.palette.background.cyan.main}
                src="/s2/icon/camera-outline.svg"
                width={16}
              />
            </Box>
            <Text
              size="sm"
              weight="medium"
            >
              Take photo
            </Text>
          </BoxStyled>
        )}
        {loading && (
          <Skeleton
            aria-label="skeleton"
            height="72px"
            variant="rectangular"
            width="128px"
          />
        )}
      </CapturedImageWrapperStyled>
      {errorMessage && !imageData && <WarningMessage message={errorMessage} />}
      {warningMessage && <WarningMessage message={warningMessage} />}
    </Stack>
  );
};

CapturedImage.propTypes = {
  sideType: PropTypes.string.isRequired,
  imageData: PropTypes.string,
  saveCapturedImageHandler: PropTypes.func.isRequired,
  isDisable: PropTypes.bool,
  loading: PropTypes.bool,
  errorMessage: PropTypes.string,
  warningMessage: PropTypes.string
};

export default memo(CapturedImage);
